<template>
    <div>
        <x-layout v-if="client" mb-5>
            <x-flex>
                <x-card class="panel">
                    <x-card-title>
                        <x-layout align-items-center>
                            <x-flex xs12>
                                <h3>Entity associations / {{ client.name }}</h3>
                            </x-flex>

                            <x-flex text-xs-right>
                                <x-btn
                                    v-if="$root.userIsBrokerAdmin()"
                                    class="ma-0"
                                    color="secondary"
                                    @click="openEntityDialog(false)"
                                >
                                    Create new entity
                                </x-btn>
                            </x-flex>
                        </x-layout>
                    </x-card-title>

                    <x-card-text>
                        <x-layout>
                            <x-flex xs12>
                                <x-data-table
                                    class="inverse no-row-hover dashed-rows mt-2"
                                    :headers="headers"
                                    :items="clientEntities"
                                    hide-actions
                                >
                                    <template v-slot:no-data>
                                        <p class="error-message text-xs-center">No entities found</p>
                                    </template>

                                    <template v-slot:items="props">
                                        <td>
                                            {{ props.item.name }}
                                        </td>
                                        <td>
                                            {{ props.item.address?.line1 || '' }}
                                        </td>
                                        <td>
                                            {{ props.item.address?.postcode || '' }}
                                        </td>
                                        <td>
                                            {{ props.item.address?.country?.name || '' }}
                                        </td>
                                        <td>
                                            {{ props.item.status ? 'Active' : 'Inactive' }}
                                        </td>
                                        <td>
                                            <x-btn
                                                color="tertiary"
                                                @click="openEntityDialog(true, props.item.id)"
                                            >
                                                Edit
                                            </x-btn>
                                        </td>
                                    </template>
                                </x-data-table>
                            </x-flex>
                        </x-layout>
                    </x-card-text>
                </x-card>
            </x-flex>
        </x-layout>

        <x-layout>
            <x-flex>
                <x-card class="panel">
                    <x-card-title>
                        <x-layout align-items-center mb-3>
                            <x-flex>
                                <h3 class="font-weight-regular">Attach an entity</h3>
                            </x-flex>

                            <x-flex text-xs-right>
                                <x-btn
                                    v-if="$root.userIsBrokerAdmin() && !client"
                                    class="ma-0"
                                    color="secondary"
                                    @click="openEntityDialog(false)"
                                >
                                    Create new entity
                                </x-btn>
                            </x-flex>
                        </x-layout>
                    </x-card-title>

                    <x-card-text>
                        <x-layout>
                            <x-flex>
                                <x-autocomplete
                                    v-model="selectedEntityId"
                                    :items="entities"
                                    label="Select Entity"
                                    placeholder="Select an Entity"
                                    item-text="nameAddress"
                                    item-value="id"
                                    append-icon="keyboard_arrow_down"
                                />

                                <x-btn
                                    class="mt-3"
                                    color="secondary"
                                    v-if="showAttachEntityButton(selectedEntity, client)"
                                    @click="attachEntity()"
                                >
                                    Attach entity
                                </x-btn>
                            </x-flex>
                        </x-layout>

                        <template v-if="selectedEntity">
                            <x-layout>
                                <x-flex xs12>
                                    <data-list v-model="entityData" class="my-4"/>
                                </x-flex>
                            </x-layout>

                            <x-layout>
                                <x-flex xs12 class="text-xs-right">
                                    <x-btn
                                        class="ma-0"
                                        color="tertiary"
                                        @click="openEntityDialog(true)"
                                    >
                                        Edit entity
                                    </x-btn>
                                </x-flex>
                            </x-layout>
                        </template>
                    </x-card-text>
                </x-card>
            </x-flex>
        </x-layout>

        <x-dialog v-model="showEntityForm" persistent width="800" content-class="overflow-scroll">
            <entity-form
                v-if="showEntityForm"
                :entity="selectedEntity"
                :edit="isEditing"
                @close="showEntityForm = false"
                @update="fetchData"
            />
        </x-dialog>
    </div>
</template>

<script>
import EntityForm from '~/components/Broker/Entity/Form';
import DataList from '~/components/Shared/UIComponents/DataList';

export default {
    name: 'EntityManager',
    components: {
        DataList,
        EntityForm
    },
    props: {
        client: {
            type: Object,
            required: false
        }
    },
    data() {
        return {
            entities: [],
            selectedEntityId: null,
            loading: true,
            isEditing: false,
            showEntityForm: false,
            headers: [
                {
                    text: 'Entity Name',
                    value: 'entity_name'
                },
                {
                    text: '1st line address',
                    value: 'line_1'
                },
                {
                    text: 'Postcode',
                    value: 'postcode'
                },
                {
                    text: 'Country',
                    value: 'country'
                },
                {
                    text: 'Status',
                    value: 'status'
                },
                {
                    text: 'Edit',
                    value: 'edit'
                },
            ],
            clientEntities: []
        };
    },
    created() {
        this.fetchData();
    },
    methods: {
        async fetchData() {
            this.loading = true;
            await axios.get(this.$root.api('entity')).then((res) => {
                this.entities = res.data.data.map(entity => {
                    entity.nameAddress = entity.name + ' - ' + (entity.address?.line1 || 'N/A') + ', ' + (entity.address?.country?.name || 'N/A');
                    return entity;
                });
            });
            if (this.client) {
                this.clientEntities = this.entities.filter(entity => entity.client_company_id === this.client.id);
            }
            this.loading = false;
        },
        openEntityDialog(isEditing, entityId = null) {
            if (entityId) {
                this.selectedEntityId = entityId;
            }
            this.isEditing = isEditing;
            this.showEntityForm = true;
        },
        async attachEntity() {
            await (axios.put(this.$root.api(`broker/entity/${this.selectedEntityId}/client/${this.client?.id}`)));
            this.fetchData();
        },
        showAttachEntityButton(selectedEntity, client) {
            return Boolean(selectedEntity) && Boolean(client) && !selectedEntity.client_company_id;
        }
    },
    watch: {
        client() {
            if (this.client) {
                this.clientEntities = this.entities.filter(entity => entity.client_company_id === this.client.id);
            }
        }
    },
    computed: {
        selectedEntity() {
            return this.entities.find(entity => entity.id === this.selectedEntityId);
        },
        entityData() {
            const data = this.selectedEntity;
            return [
                {
                    text: 'Entity name',
                    value: data.name
                },
                {
                    text: 'Building number',
                    value: data.address?.line3
                },
                {
                    text: 'Address line 1',
                    value: data.address?.line1
                },
                {
                    text: 'Address line 2',
                    value: data.address?.line2
                },
                {
                    text: 'City/Town',
                    value: data.address?.city
                },
                {
                    text: 'Post code',
                    value: data.address?.postcode
                },
                {
                    text: 'Country',
                    value: data.address?.country?.name
                },
                {
                    text: 'Latitude',
                    value: data.address?.latitude
                },
                {
                    text: 'Longitude',
                    value: data.address?.longitude
                },
                {
                    text: 'DUNS reference',
                    value: data.address?.duns_ref
                },
                {
                    text: 'HIBL client code',
                    value: data.hibl_client_code
                },
                {
                    text: 'LUX client code',
                    value: data.lux_client_code
                },
            ];
        }
    }
};
</script>

<style lang="scss" scoped>
.main-container {
    background: black;
    border-radius: 5px;
}
.align-items-center {
    align-items: center;
}
.flex-column {
    flex-direction: column;
}
</style>
